import { useEffect, useState } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { api, downloadBlob, apiErrorMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Sparkles, Loader2, Save, ExternalLink, FileDown, Send, Eye } from "lucide-react";
import { toast } from "sonner";
import FormField from "@/components/FormField";
import { scoreAnswers, bandColor, allFields } from "@/lib/forms";
import { SourcePill, StatusPill, AudiencePill, DraftBanner } from "@/components/forms/FormBadges";
import { AiConfirmDialog, AiDraftReview, SubmitReviewDialog, missingRequired } from "@/components/forms/FormDialogs";
import GovernancePanel from "@/components/forms/GovernancePanel";
import { useAuth } from "@/context/AuthContext";

function ScoreCard({ t, score }) {
  return (
    <Card data-testid="live-score" className="p-5 bg-gradient-to-br from-[#0A2A5E] to-[#0F3F8A] text-white border-0">
      <div className="text-xs uppercase tracking-widest text-[#F5A623] font-bold">Live score</div>
      <div className="font-display text-4xl font-extrabold mt-1">{score ? `${score.percent}%` : "—"}</div>
      <div className="text-sm text-slate-300 mt-1">{score ? `${score.earned} / ${score.possible} weighted points` : "Rate items to see the score"}</div>
      {score && <div className={`mt-3 inline-block px-3 py-1 rounded-full text-sm font-semibold ${bandColor(score.percent)}`} data-testid="live-band">{score.band}</div>}
      <div className="mt-4 text-[11px] text-slate-300 space-y-0.5">{t.bands.map(([c, l]) => <div key={l} className="flex justify-between"><span>{l}</span><span>≥ {c}%</span></div>)}</div>
    </Card>
  );
}

export default function FormFill() {
  const { templateId } = useParams();
  const [params] = useSearchParams();
  const draftId = params.get("draft");
  const nav = useNavigate();
  const { user } = useAuth();
  const [t, setT] = useState(null);
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [aiKind, setAiKind] = useState(null); // pending confirmation: "assist" | "explain"
  const [explainField, setExplainField] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiDraft, setAiDraft] = useState(null);
  const [explanations, setExplanations] = useState({});
  const [review, setReview] = useState(false);
  const [recipients, setRecipients] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [savedDraftId, setSavedDraftId] = useState(draftId);

  useEffect(() => {
    api.get(`/forms/templates/${templateId}`).then((r) => setT(r.data)).catch((e) => { toast.error(apiErrorMessage(e, "Template not found")); nav("/app/forms"); });
    api.get("/forms/recipients").then((r) => setRecipients(r.data)).catch(() => {});
    if (draftId) api.get(`/forms/submissions/${draftId}`).then((r) => { if (r.data.status === "draft") setAnswers(r.data.answers || {}); }).catch(() => toast.error("Draft not found"));
  }, [templateId, draftId, nav]);

  const set = (id, v) => { setAnswers((a) => ({ ...a, [id]: v })); if (errors[id]) setErrors((e) => ({ ...e, [id]: undefined })); };
  const score = t ? scoreAnswers(t, answers) : null;

  const persist = async (status) => {
    const body = { answers, status, confirm_recipients: status === "submitted" && confirmed };
    if (savedDraftId) return (await api.put(`/forms/submissions/${savedDraftId}`, body)).data;
    return (await api.post("/forms/submissions", { template_id: t.id, ...body })).data;
  };

  const saveDraft = async () => {
    setBusy(true);
    try { const d = await persist("draft"); setSavedDraftId(d.id); toast.success("Private draft saved — visible only to you"); }
    catch (e) { toast.error(apiErrorMessage(e, "Could not save")); }
    finally { setBusy(false); }
  };

  const openReview = async () => {
    const missing = missingRequired(t, answers);
    if (missing.length) {
      setErrors(Object.fromEntries(missing.map((f) => [f.id, "This field is required"])));
      toast.error(`Please complete: ${missing.map((f) => f.label).join(", ")}`);
      document.getElementById(`field-${missing[0].id}`)?.focus();
      return;
    }
    try { setRecipients((await api.get("/forms/recipients")).data); } catch { toast.error("Could not load recipients"); return; }
    setConfirmed(false);
    setReview(true);
  };

  const submit = async () => {
    setBusy(true);
    try { const d = await persist("submitted"); toast.success("Form submitted to HR Managers"); nav(`/app/forms/submissions/${d.id}`); }
    catch (e) { toast.error(apiErrorMessage(e, "Could not submit")); }
    finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    try { await downloadBlob("/forms/pdf/preview", `${t.id}-preview.pdf`, { method: "POST", data: { template_id: t.id, answers } }); toast.success("PDF downloaded"); }
    catch (e) { toast.error(apiErrorMessage(e, "Could not generate PDF")); }
  };

  const runAi = async () => {
    const kind = aiKind; setAiKind(null); setAiBusy(true);
    try {
      if (kind === "assist") { const { data } = await api.post("/forms/assist", { template_id: t.id, answers }); setAiDraft(data); }
      else { const { data } = await api.post("/forms/explain", { template_id: t.id, field_id: explainField.id }); setExplanations((x) => ({ ...x, [explainField.id]: data })); }
    } catch (e) { toast.error(apiErrorMessage(e, "AI action failed")); }
    finally { setAiBusy(false); }
  };
  const regenerate = async () => { setAiBusy(true); try { const { data } = await api.post("/forms/assist", { template_id: t.id, answers }); setAiDraft(data); } catch (e) { toast.error(apiErrorMessage(e)); } finally { setAiBusy(false); } };

  if (!t) return <div className="p-10 text-center text-slate-500">Loading…</div>;
  const assistLabel = allFields(t).find((f) => f.id === t.assist_field)?.label;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-28 lg:pb-6">
      <Link to="/app/forms" data-testid="back-to-forms" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-[#0A2A5E] mb-4"><ArrowLeft className="w-4 h-4" aria-hidden="true" /> Forms Center</Link>
      {t.banner && <div className="mb-4"><DraftBanner text={t.banner} status={t.status} /></div>}
      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-5">
          <div>
            <div className="flex flex-wrap gap-1.5 mb-2"><SourcePill source={t.source_kind} label={t.source_label} /><StatusPill status={t.status} label={t.status_label} /><AudiencePill audience={t.audience} label={t.audience === "manager" ? "HR Managers / Supervisors" : "Employees and Managers"} /></div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#C7761A] font-bold">{t.category}</div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0A2A5E]">{t.title}</h1>
            <p className="text-sm text-slate-600 mt-1">{t.description}</p>
            <p className="text-[11px] text-slate-500 mt-1" data-testid="template-meta">Version {t.version}{t.status === "approved" && ` · ${t.approval_status}${t.effective_date ? ` · effective ${t.effective_date}` : ""}`}{t.status === "beta" && " · Beta — review before use"} · {savedDraftId ? "Continuing your private draft" : "Blank template"} · Prepared for use by: {recipients?.org_name || "your organization"}</p>
            {t.status === "retired" && <p role="alert" data-testid="retired-notice" className="text-xs text-rose-700 font-semibold mt-1">Retired — not available for new use (visible to LUMIS HR reviewers only).</p>}
            {t.source && <a href={t.source.source_url} target="_blank" rel="noreferrer" className="text-xs text-slate-500 hover:underline inline-flex items-center gap-1 mt-1">Reference guide: {t.source.citation} <ExternalLink className="w-3 h-3" aria-hidden="true" /></a>}
            <p role="note" data-testid="fill-sensitive-warning" className="text-[11px] text-[#7C3F00] font-medium mt-2">{t.sensitive_warning}</p>
            {t.purpose_note && <p className="text-xs text-slate-600 mt-1 italic">{t.purpose_note}</p>}
          </div>

          {t.sections.map((s, i) => (
            <Card key={s.title || i} data-testid={`section-${i}`} className="p-5 sm:p-6 bg-white border-slate-200">
              <h2 className="font-display font-bold text-[#0A2A5E] mb-4 flex items-center gap-2"><span className="w-1.5 h-5 rounded-full bg-[#F5A623]" aria-hidden="true" />{s.title}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {s.fields.map((f) => <FormField key={f.id} field={f} value={answers[f.id]} onChange={set} error={errors[f.id]} explanation={explanations[f.id]} onExplain={(fld) => { setExplainField(fld); setAiKind("explain"); }} />)}
              </div>
            </Card>
          ))}
          <footer data-testid="template-disclaimer" className="text-[11px] text-slate-500 leading-relaxed border-t border-slate-200 pt-3">
            <p>{t.disclaimer}</p><p className="mt-1">Created by LUMIS HR Solutions LLC · {t.copyright}</p>
          </footer>
        </div>

        <aside className="lg:col-span-4">
          <div className="sticky top-24 space-y-4">
            {user?.is_lumis_admin && t.governed && <GovernancePanel template={t} onUpdated={setT} />}
            {t.bands && <ScoreCard t={t} score={score} />}
            {assistLabel && (
              <Card className="p-5 bg-white border-slate-200">
                <div className="text-xs uppercase tracking-widest text-[#C7761A] font-bold">Optional AI help</div>
                <p className="text-sm text-slate-600 mt-1">Draft <span className="font-semibold">{assistLabel}</span> from what you have entered. Runs only when you choose it; you review before anything is inserted.</p>
                <Button data-testid="ai-assist-btn" onClick={() => setAiKind("assist")} disabled={aiBusy} variant="outline" className="w-full mt-3 border-[#F5A623] text-[#7C3F00] hover:bg-[#FDECD0]">
                  {aiBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" /> : <Sparkles className="w-4 h-4 mr-2" aria-hidden="true" />} Draft with AI
                </Button>
              </Card>
            )}
            <div className="flex flex-col gap-2">
              <Button data-testid="submit-form-btn" onClick={openReview} disabled={busy} className="bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white h-11"><Send className="w-4 h-4 mr-2" aria-hidden="true" /> Review and submit</Button>
              <Button data-testid="save-draft-btn" onClick={saveDraft} disabled={busy} variant="outline"><Save className="w-4 h-4 mr-2" aria-hidden="true" /> Save private draft</Button>
              <Button data-testid="preview-pdf-btn" onClick={downloadPdf} variant="outline" className="border-slate-300"><FileDown className="w-4 h-4 mr-2" aria-hidden="true" /> Download PDF (personal copy)</Button>
              <Link to={`/app/forms/print/${t.id}`}><Button variant="ghost" className="w-full text-slate-600" data-testid="preview-blank-btn"><Eye className="w-4 h-4 mr-2" aria-hidden="true" /> Preview blank template</Button></Link>
            </div>
            <p className="text-[11px] text-slate-500">Saving a draft keeps it private to you. Nothing is submitted until you review the recipients and confirm.</p>
          </div>
        </aside>
      </div>
      <div data-testid="mobile-submit-bar" className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-t border-slate-200 p-3 flex items-center gap-2">
        {score && <div className="text-xs text-slate-600 mr-auto"><span className="font-display font-extrabold text-[#0A2A5E] text-lg">{score.percent}%</span> {score.band}</div>}
        <Button data-testid="save-draft-btn-mobile" onClick={saveDraft} disabled={busy} variant="outline" className="h-10">Save draft</Button>
        <Button data-testid="submit-form-btn-mobile" onClick={openReview} disabled={busy} className="bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white h-10 flex-1 max-w-[12rem]"><Send className="w-4 h-4 mr-2" aria-hidden="true" /> Review</Button>
      </div>

      <AiConfirmDialog kind={aiKind} onConfirm={runAi} onCancel={() => setAiKind(null)} />
      <AiDraftReview draft={aiDraft} label={t.ai_label} busy={aiBusy} onAccept={() => { set(aiDraft.field, aiDraft.text); setAiDraft(null); toast.success("Inserted — review and edit before saving"); }} onReject={() => setAiDraft(null)} onRegenerate={regenerate} />
      <SubmitReviewDialog open={review} onOpenChange={setReview} template={t} answers={answers} recipients={recipients} confirmed={confirmed} setConfirmed={setConfirmed} onSubmit={submit} busy={busy} />
    </div>
  );
}
